import type { ConnectionState } from '../connection/connectionSlice'
import type { LiveStatus } from '../live/types'
import { formatClock } from './time'
import type { HealthSignal, HealthSubject } from './types'

export const SERVER_SUBJECT: HealthSubject = { kind: 'server', id: 'server', title: 'Сервер программы' }

/** Ключ действия «Повторить»: проверить сервер сейчас, не дожидаясь паузы (исполняет оболочка). */
export const SERVER_RETRY_ACTION = 'retry'

const iso = (ms: number | null): string | null => (ms === null ? null : new Date(ms).toISOString())

/** Текст для администратора: вид, код и номер запроса последней ошибки связи. */
function diagnosticsOf(connection: ConnectionState): string | null {
  if (connection.status === 'online') return null
  const error = connection.lastError
  return [
    `Сервер программы: ${connection.status === 'offline' ? 'нет сети на устройстве' : 'не отвечает'}`,
    ...(connection.since !== null ? [`Без связи с: ${iso(connection.since)}`] : []),
    ...(connection.lastOkAt !== null ? [`Последний ответ: ${iso(connection.lastOkAt)}`] : []),
    `Проверок: ${connection.attempts}`,
    ...(error !== null
      ? [
          `Ошибка: ${error.message}`,
          `Вид: ${error.kind}; код: ${error.code}${error.status !== undefined ? `; HTTP ${error.status}` : ''}`,
          ...(error.correlationId !== null ? [`Номер запроса: ${error.correlationId}`] : []),
        ]
      : []),
  ].join('\n')
}

/**
 * Связь с нашим сервером одним сигналом (RP-UI-LINK, шаг 2): срез `connection` (дошёл ли запрос) и живой канал (идут ли
 * обновления сами).
 * - нет сети на устройстве или сервер не отвечает — `down`;
 * - сервер отвечает, но живой канал переподключается — `degraded`: работать можно, данные на экране могут отставать;
 * - иначе — `ok` (канал выключен или только подключается — это не сбой).
 */
export function serverHealth(connection: ConnectionState, live: LiveStatus): HealthSignal {
  // «Последний ответ» нужен, пока сервер молчит; отвечающему сервису он — шум (о связи говорим только при сбое).
  const lastOkAt = connection.status === 'online' ? null : iso(connection.lastOkAt)
  if (connection.status !== 'online') {
    const offline = connection.status === 'offline'
    const at = formatClock(connection.since)
    return {
      subject: SERVER_SUBJECT,
      level: 'down',
      summary: offline ? 'Нет интернета на этом устройстве' : at === null ? 'Сервер не отвечает' : `Сервер не отвечает с ${at}`,
      problem: connection.lastError?.message ?? null,
      lastOkAt,
      since: iso(connection.since),
      advice: offline
        ? 'Проверьте Wi-Fi или кабель. Когда сеть появится, программа сама проверит сервер.'
        : 'Проверьте интернет. Программа проверяет сервер сама; «Повторить» — проверить сейчас.',
      actions: [{ key: SERVER_RETRY_ACTION, label: 'Повторить' }],
      diagnostics: diagnosticsOf(connection),
    }
  }
  if (live === 'reconnecting') {
    return {
      subject: SERVER_SUBJECT,
      level: 'degraded',
      summary: 'Обновления в реальном времени прерваны — данные на экране могут отставать',
      problem: null,
      lastOkAt,
      since: null,
      advice: 'Ничего делать не нужно: программа переподключается сама. Если долго не проходит — обновите страницу.',
      actions: [],
      diagnostics: null,
    }
  }
  return {
    subject: SERVER_SUBJECT,
    level: 'ok',
    summary: 'Отвечает',
    problem: null,
    lastOkAt,
    since: null,
    advice: null,
    actions: [],
    diagnostics: null,
  }
}
